import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Minus, Plus, Trash2, Undo2, Wand2, Loader2, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { EinweisungGruppe } from '../sige-plan/types';
import { STICHPUNKT_MAX_ZEICHEN } from '../../services/sigeEinweisung';
import {
  SeitenAnzeige,
  braucheGruppenRueckfrage,
  entfernePunkt,
  entferneGruppe,
  fuegeGruppeHinzu,
  fuegePunktEin,
  fuegePunktHinzu,
  setzeGruppenTitel,
  setzePunkt,
  verschiebePunkt,
  zaehlePunkte,
} from './einweisungLogic';

interface Props {
  gruppen: EinweisungGruppe[];
  onChange: (gruppen: EinweisungGruppe[]) => void;
  /** Live-Anzeige aus schaetzeSeiten — steht dauerhaft im Blick, sperrt nie. */
  anzeige: SeitenAnzeige;
  onVorschlag: () => void;
  vorschlagLaeuft?: boolean;
  isReadOnly?: boolean;
  isMobile?: boolean;
}

/** Ein geloeschter Punkt, solange die Ruecknahme angeboten wird. */
interface GeloeschterPunkt {
  gruppenId: string;
  index: number;
  text: string;
}

/**
 * Das Kernstueck: die Stichpunkte des Einweisungsblattes.
 *
 * Bedienung bewusst mit Knoepfen statt Ziehen-und-Ablegen — die Einweisung
 * wird auf der Baustelle am Handy gehalten, mit Handschuh und in der Sonne.
 * Ein Pfeil, den man treffen kann, schlaegt jede Drag-Geste.
 *
 * ─── Warum die Zeile auf dem Handy anders aufgebaut ist ────────────────────
 * Auf dem Desktop stehen Pfeile, Text und Minus nebeneinander. Auf dem Handy
 * waere ein Pfeil dann 22 px hoch — unter der Groesse, die ein behandschuhter
 * Finger sicher trifft. Dort steht der Text deshalb oben und die Knoepfe
 * darunter in einer eigenen Reihe, jeder 44 px hoch. Zwischen den Pfeilen und
 * dem Minus liegt ein Abstand, der die Reihe auseinanderzieht: ein Fehlgriff
 * beim Verschieben darf nicht den Punkt loeschen.
 *
 * Und wo doch danebengegriffen wird, kostet es nichts — jeder geloeschte Punkt
 * laesst sich unmittelbar zurueckholen.
 *
 * Alle Aenderungen laufen ueber die puren Funktionen aus einweisungLogic.ts;
 * diese Datei ordnet nur an.
 */
export const PunkteEditor: React.FC<Props> = ({
  gruppen,
  onChange,
  anzeige,
  onVorschlag,
  vorschlagLaeuft = false,
  isReadOnly = false,
  isMobile = false,
}) => {
  const gesperrt = isReadOnly;
  const knopf = isMobile ? 'min-h-[44px] min-w-[44px]' : 'min-h-[32px] min-w-[32px]';
  const [geloescht, setGeloescht] = useState<GeloeschterPunkt | null>(null);

  const neueGruppe = () => {
    const titel = window.prompt('Überschrift der neuen Gruppe:', 'Weitere Hinweise');
    if (!titel?.trim()) return;
    onChange(fuegeGruppeHinzu(gruppen, `eigen-${crypto.randomUUID()}`, titel.trim()));
  };

  /** Loeschen mit Gedaechtnis — die Ruecknahme steht danach bereit. */
  const punktLoeschen = (gruppenId: string, index: number, text: string) => {
    setGeloescht({ gruppenId, index, text });
    onChange(entfernePunkt(gruppen, gruppenId, index));
  };

  const punktZurueck = () => {
    if (!geloescht) return;
    onChange(fuegePunktEin(gruppen, geloescht.gruppenId, geloescht.index, geloescht.text));
    setGeloescht(null);
  };

  /**
   * Eine Gruppe MIT Inhalt nimmt beim Loeschen alle ihre Punkte mit. Das ist
   * mehr, als eine Ruecknahme-Zeile auffangen sollte — deshalb hier die
   * Rueckfrage und nicht dort.
   */
  const gruppeLoeschen = (gruppe: EinweisungGruppe) => {
    if (braucheGruppenRueckfrage(gruppe)) {
      const anzahl = gruppe.punkte.filter((p) => (p || '').trim()).length;
      const ok = window.confirm(
        `„${gruppe.titel || 'Ohne Überschrift'}" mit ${anzahl} ${anzahl === 1 ? 'Punkt' : 'Punkten'} entfernen?`,
      );
      if (!ok) return;
    }
    onChange(entferneGruppe(gruppen, gruppe.id));
  };

  return (
    <div className="space-y-4">
      {/* Kopfzeile: Vorschlag + die dauerhaft sichtbare Seitenanzeige. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onVorschlag}
            disabled={gesperrt || vorschlagLaeuft}
            className={`${knopf} px-4 py-2 flex items-center gap-2 text-sm font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-xl transition-colors disabled:opacity-50`}
          >
            {vorschlagLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {vorschlagLaeuft ? 'Liest den Plan…' : 'Vorschlag aus dem Plan'}
          </button>
          <span className="text-xs text-slate-400">{zaehlePunkte(gruppen)} Punkte</span>
        </div>

        {/* Die Schaetzung faerbt, sie sperrt nicht: die verbindliche Seitenzahl
            kennt erst der PDF-Generator (siehe schaetzeSeiten). */}
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border ${
            anzeige.ton === 'ok'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-800 border-amber-300'
          }`}
        >
          {anzeige.ton === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {anzeige.text}
        </div>
      </div>

      {/* Ruecknahme des letzten geloeschten Punktes. Steht bewusst OBEN und
          bleibt stehen, bis sie genutzt oder weggeklickt wird — auf der
          Baustelle bemerkt man den Fehlgriff manchmal erst zwei Zeilen
          spaeter, eine nach drei Sekunden verschwundene Meldung waere dann
          nutzlos. */}
      {geloescht && !gesperrt && (
        <div className="p-2 pl-3 bg-slate-800 rounded-xl flex items-center gap-2">
          <span className="flex-1 min-w-0 text-xs text-slate-200 truncate">
            Punkt entfernt{geloescht.text.trim() ? `: „${geloescht.text.trim()}"` : ''}
          </span>
          <button
            type="button"
            onClick={punktZurueck}
            className="min-h-[44px] px-3 shrink-0 flex items-center gap-1.5 text-xs font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Undo2 className="w-3.5 h-3.5" /> Zurückholen
          </button>
          <button
            type="button"
            onClick={() => setGeloescht(null)}
            title="Hinweis schließen"
            className="min-h-[44px] min-w-[44px] shrink-0 flex items-center justify-center text-slate-400 hover:text-white rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {gruppen.length === 0 && (
        <div className="p-6 bg-slate-50 border border-dashed border-slate-300 rounded-xl text-sm text-slate-500 text-center">
          Noch keine Punkte. „Vorschlag aus dem Plan" füllt das Blatt aus dem SiGe-Plan —
          ergänzen und streichen lässt sich danach alles.
        </div>
      )}

      {gruppen.map((gruppe) => (
        <div key={gruppe.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
            <input
              value={gruppe.titel}
              onChange={(e) => onChange(setzeGruppenTitel(gruppen, gruppe.id, e.target.value))}
              disabled={gesperrt}
              className="flex-1 bg-transparent text-sm font-bold text-slate-800 outline-none disabled:text-slate-500"
              placeholder="Überschrift"
            />
            <button
              type="button"
              onClick={() => onChange(fuegePunktHinzu(gruppen, gruppe.id))}
              disabled={gesperrt}
              title="Punkt hinzufügen"
              className={`${knopf} flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40`}
            >
              <Plus className="w-4 h-4" />
            </button>
            {/* Abstand zum Plus: die beiden tun Gegensaetzliches. */}
            <button
              type="button"
              onClick={() => gruppeLoeschen(gruppe)}
              disabled={gesperrt}
              title="Gruppe entfernen"
              className={`${knopf} ml-1 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="divide-y divide-slate-100">
            {gruppe.punkte.length === 0 && (
              <p className="px-3 py-3 text-xs text-slate-400">Kein Punkt — über das Plus oben ergänzen.</p>
            )}
            {gruppe.punkte.map((punkt, index) => {
              const zuLang = punkt.length > STICHPUNKT_MAX_ZEICHEN;
              const pfeilKlasse = isMobile
                ? 'min-h-[44px] min-w-[44px]'
                : 'min-h-[16px] min-w-[24px]';

              const hoch = (
                <button
                  type="button"
                  onClick={() => onChange(verschiebePunkt(gruppen, gruppe.id, index, -1))}
                  disabled={gesperrt || index === 0}
                  title="Nach oben"
                  className={`${pfeilKlasse} flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-20`}
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              );

              const runter = (
                <button
                  type="button"
                  onClick={() => onChange(verschiebePunkt(gruppen, gruppe.id, index, 1))}
                  disabled={gesperrt || index === gruppe.punkte.length - 1}
                  title="Nach unten"
                  className={`${pfeilKlasse} flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-20`}
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              );

              const minus = (
                <button
                  type="button"
                  onClick={() => punktLoeschen(gruppe.id, index, punkt)}
                  disabled={gesperrt}
                  title="Punkt entfernen"
                  className={`${knopf} shrink-0 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40`}
                >
                  <Minus className="w-4 h-4" />
                </button>
              );

              const textfeld = (
                <textarea
                  value={punkt}
                  onChange={(e) => onChange(setzePunkt(gruppen, gruppe.id, index, e.target.value))}
                  disabled={gesperrt}
                  rows={isMobile ? 2 : 1}
                  className={`w-full flex-1 resize-y text-sm text-slate-700 bg-transparent px-2 py-1.5 rounded-lg outline-none focus:bg-slate-50 disabled:text-slate-500 ${zuLang ? 'ring-1 ring-amber-300' : ''}`}
                  placeholder="Stichpunkt"
                />
              );

              // Handy: Text oben, Knoepfe darunter mit 44 px und mit Abstand
              // zwischen Verschieben und Loeschen.
              if (isMobile) {
                return (
                  <div key={index} className="px-2 py-2 space-y-1">
                    {textfeld}
                    <div className="flex items-center gap-1">
                      {hoch}
                      {runter}
                      <span className="flex-1" />
                      {minus}
                    </div>
                  </div>
                );
              }

              return (
                <div key={index} className="flex items-start gap-1.5 px-2 py-1.5">
                  <div className="flex flex-col shrink-0">{hoch}{runter}</div>
                  {textfeld}
                  {minus}
                </div>
              );
            })}
          </div>

          {/* Hinweis, kein Riegel: ein langer Punkt wird im PDF umbrochen, er
              kostet nur eine zweite Zeile. */}
          {gruppe.punkte.some((p) => p.length > STICHPUNKT_MAX_ZEICHEN) && (
            <p className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100">
              Ein Punkt über {STICHPUNKT_MAX_ZEICHEN} Zeichen braucht auf dem Blatt zwei Zeilen.
            </p>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={neueGruppe}
        disabled={gesperrt}
        className={`${knopf} w-full px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 bg-white border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-40`}
      >
        <Plus className="w-4 h-4" /> Gruppe hinzufügen
      </button>
    </div>
  );
};
